import { addHours, dateIsBefore } from 'lib/datetime/dateUtils'

/** Minimum time between showings of the modal, across all tabs */
export const LUX_PLUS_INVALID_CARD_MODAL_COOLDOWN_HOURS = 24
/** Most times the modal shows for the same set of invalid cards */
export const MAX_LUX_PLUS_INVALID_CARD_MODAL_VIEWS = 3

export interface LuxPlusInvalidCardModalViewRecord {
  /** Payment method ids of the invalid LP+ cards the modal was last shown for */
  flaggedPaymentMethodIds: Array<string>
  count: number
  /** ISO timestamp of the last showing. Missing on records stored before the cooldown existed. */
  lastShownAt?: string
}

export function getLuxPlusInvalidCardModalStorageKey(memberId: string) {
  return `luxPlusInvalidCardModal:${memberId}`
}

/**
 * The view record that applies to the current invalid cards.
 * When every current card was already flagged (same set, or some since removed) the stored count carries on;
 * when any card is new it starts a fresh record, so the modal shows straight away and the cooldown restarts from there.
 */
export function getLuxPlusInvalidCardModalViewRecord(
  currentPaymentMethodIds: Array<string>,
  storedRecord: LuxPlusInvalidCardModalViewRecord | undefined,
): LuxPlusInvalidCardModalViewRecord {
  const isAlreadyFlagged = !!storedRecord && currentPaymentMethodIds.every(id => storedRecord.flaggedPaymentMethodIds.includes(id))
  if (storedRecord && isAlreadyFlagged) return storedRecord

  return { flaggedPaymentMethodIds: currentPaymentMethodIds, count: 0 }
}

function isLuxPlusInvalidCardModalViewRecord(value: unknown): value is LuxPlusInvalidCardModalViewRecord {
  if (!value || typeof value !== 'object') return false

  const record = value as Partial<LuxPlusInvalidCardModalViewRecord>
  const hasValidLastShownAt = record.lastShownAt === undefined || typeof record.lastShownAt === 'string'
  return Array.isArray(record.flaggedPaymentMethodIds) && typeof record.count === 'number' && hasValidLastShownAt
}

/**
 * Records a view of the modal for these invalid cards, unless it was shown within the cooldown
 * or has hit the view limit for them. Storage access errors throw, so the caller can skip the modal.
 * @returns whether the modal should show now
 */
export function recordLuxPlusInvalidCardModalView(memberId: string, currentPaymentMethodIds: Array<string>, now: Date = new Date()): boolean {
  const storageKey = getLuxPlusInvalidCardModalStorageKey(memberId)
  const storedValue = window.localStorage.getItem(storageKey)
  let parsedValue: unknown
  if (storedValue) {
    try {
      parsedValue = JSON.parse(storedValue)
    } catch {
      // Corrupt value: start a fresh record so the write below overwrites it
      parsedValue = undefined
    }
  }
  const storedRecord = isLuxPlusInvalidCardModalViewRecord(parsedValue) ? parsedValue : undefined
  const record = getLuxPlusInvalidCardModalViewRecord(currentPaymentMethodIds, storedRecord)

  const cooldownEndsAt = record.lastShownAt ? addHours(new Date(record.lastShownAt), LUX_PLUS_INVALID_CARD_MODAL_COOLDOWN_HOURS) : undefined
  const isWithinCooldown = !!cooldownEndsAt && dateIsBefore(now, cooldownEndsAt)
  if (isWithinCooldown) return false
  if (record.count >= MAX_LUX_PLUS_INVALID_CARD_MODAL_VIEWS) return false

  const nextRecord: LuxPlusInvalidCardModalViewRecord = { ...record, count: record.count + 1, lastShownAt: now.toISOString() }
  window.localStorage.setItem(storageKey, JSON.stringify(nextRecord))
  return true
}
